import { z } from 'zod';

import { CreditAgreement } from '@finley-technologies/finley-types';

const generateJsonSchema = (zodSchema: z.ZodTypeAny): string => {
  const jsonSchema = z.toJSONSchema(zodSchema, {
    target: 'draft-7',
  });
  return JSON.stringify(jsonSchema);
};

// Pre-generated JSON schemas for each field (static constants)
export const FIELD_JSON_SCHEMAS = {
  closingDate: generateJsonSchema(
    CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape.closingDate,
  ),
  finalMaturityDate: generateJsonSchema(
    CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
      .finalMaturityDate,
  ),
  structure: generateJsonSchema(
    CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape.structure,
  ),
  amortizationPeriodStart: generateJsonSchema(
    CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
      .amortizationPeriodStart,
  ),
  amortizationPeriodEnd: generateJsonSchema(
    CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
      .amortizationPeriodEnd,
  ),
  callProtectionExpirationDate: generateJsonSchema(
    CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
      .callProtectionExpirationDate,
  ),
  callProtectionValue: generateJsonSchema(
    CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
      .callProtectionValue,
  ),
  commitmentPeriodEffectiveDate: generateJsonSchema(
    CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
      .commitmentPeriodEffectiveDate,
  ),
  governingLaw: generateJsonSchema(
    CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape.governingLaw,
  ),
  interestRateCeiling: generateJsonSchema(
    CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
      .interestRateCeiling,
  ),
  minimumUtilization: generateJsonSchema(
    CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
      .minimumUtilization,
  ),
  revolvingCommitmentTermination: generateJsonSchema(
    CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
      .revolvingCommitmentTermination,
  ),
  seniority: generateJsonSchema(
    CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape.seniority,
  ),
  yieldMaintenance: generateJsonSchema(
    CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
      .yieldMaintenance,
  ),
  externalFacilityId: generateJsonSchema(
    CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
      .externalFacilityId,
  ),
} as const;

// Base instruction template for consistent formatting
const createFieldPrompt = (
  fieldName: string,
  description: string,
  isRequired: boolean = false,
  additionalContext?: string,
): string => {
  const basePrompt = `Extract the ${fieldName} field from the PDF document. ${description}`;
  const contextualPrompt = additionalContext
    ? `${basePrompt} ${additionalContext}`
    : basePrompt;

  const emptyHandling = isRequired
    ? `If the value is not found or missing, return null as the value. NEVER use placeholder values like "<UNKNOWN>", "UNKNOWN", "TBD", "N/A", "to be determined", or any similar text - use null instead.`
    : `If the value is not found, missing, or contains placeholder text like "unknown", "<UNKNOWN>", "TBD", "N/A", "to be determined", or any similar placeholder, return an empty object {} and do not call this tool at all. This field is optional and should be omitted entirely when not found. NEVER use placeholder values - omit the field completely instead.`;

  return `${contextualPrompt} ${emptyHandling}`;
};

export const fieldsToPromptAndZodSchema: Record<
  keyof CreditAgreement.DomainEntity.BaseFacilityMechanics,
  {
    prompt: string;
    zodSchema: typeof CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape[keyof typeof CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape];
  }
> = {
  closingDate: {
    prompt: createFieldPrompt(
      'closingDate',
      'This is the closing date of the credit agreement.',
      true,
    ),
    zodSchema:
      CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
        .closingDate,
  },
  finalMaturityDate: {
    prompt: createFieldPrompt(
      'finalMaturityDate',
      'This is the final maturity date when the facility expires.',
      true,
    ),
    zodSchema:
      CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
        .finalMaturityDate,
  },
  structure: {
    prompt: createFieldPrompt(
      'structure',
      'This describes the facility structure type (e.g., revolving, term loan).',
      true,
    ),
    zodSchema:
      CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape.structure,
  },
  amortizationPeriodStart: {
    prompt: createFieldPrompt(
      'amortizationPeriodStart',
      'This is when principal amortization begins.',
      false,
      'In most credit agreements, this is the date when interest-only payments end and principal payments start according to the amortization schedule.',
    ),
    zodSchema:
      CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
        .amortizationPeriodStart,
  },
  amortizationPeriodEnd: {
    prompt: createFieldPrompt(
      'amortizationPeriodEnd',
      'This is when the amortization period ends.',
    ),
    zodSchema:
      CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
        .amortizationPeriodEnd,
  },
  callProtectionExpirationDate: {
    prompt: createFieldPrompt(
      'callProtectionExpirationDate',
      'This is the date when call protection provisions expire.',
    ),

    zodSchema:
      CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
        .callProtectionExpirationDate,
  },
  callProtectionValue: {
    prompt: createFieldPrompt(
      'callProtectionValue',
      'This is the call protection value, typically expressed as a percentage.',
    ),

    zodSchema:
      CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
        .callProtectionValue,
  },
  commitmentPeriodEffectiveDate: {
    prompt: createFieldPrompt(
      'commitmentPeriodEffectiveDate',
      'This is the effective date of the commitment period.',
    ),
    zodSchema:
      CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
        .commitmentPeriodEffectiveDate,
  },
  governingLaw: {
    prompt: createFieldPrompt(
      'governingLaw',
      'This is the governing jurisdiction for the agreement.',
      false,
      'Look for this information in the governing law section. The value should be a location like "New York" or "Delaware".',
    ),
    zodSchema:
      CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
        .governingLaw,
  },
  interestRateCeiling: {
    prompt: createFieldPrompt(
      'interestRateCeiling',
      'This is the maximum interest rate ceiling or cap.',
    ),
    zodSchema:
      CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
        .interestRateCeiling,
  },
  minimumUtilization: {
    prompt: createFieldPrompt(
      'minimumUtilization',
      'This is the minimum utilization requirement, typically expressed as a percentage.',
    ),
    zodSchema:
      CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
        .minimumUtilization,
  },
  revolvingCommitmentTermination: {
    prompt: createFieldPrompt(
      'revolvingCommitmentTermination',
      'This is the date when the revolving commitment terminates.',
    ),
    zodSchema:
      CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
        .revolvingCommitmentTermination,
  },
  seniority: {
    prompt: createFieldPrompt(
      'seniority',
      'This describes the seniority level or ranking of the facility (e.g., senior, subordinated).',
    ),
    zodSchema:
      CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape.seniority,
  },
  yieldMaintenance: {
    prompt: createFieldPrompt(
      'yieldMaintenance',
      'This is the yield maintenance expiration date.',
      false,
      'Yield maintenance is a prepayment penalty designed to keep the lender whole upon early prepayment. Extract the relevant **date** (e.g., the date the yield maintenance period ends/expires), not a percentage, amount, or formula.',
    ),
    zodSchema:
      CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
        .yieldMaintenance,
  },
  externalFacilityId: {
    prompt: createFieldPrompt(
      'externalFacilityId',
      'This is an external identifier or reference number for the facility.',
    ),
    zodSchema:
      CreditAgreement.DomainEntity.BaseFacilityMechanicsSchema.shape
        .externalFacilityId,
  },
};
